import { createReducer } from '@reduxjs/toolkit'

import { ReportPeriod } from '@/constants/report-period'
import {
  CreatorOverviewChannels,
  CreatorOverviewStatistics,
  CreatorOverviewSummary,
  CreatorOverviewTrends,
  CreatorPerformance,
  ReadCreatorOverviewSummaryParams,
  TopCreatorsSummary,
} from '@/model/analytics'
import { selectFeatureTimezone } from '@/redux/global-selector'
import { createAppAsyncThunk } from '@/redux/utils'
import { axios } from '@/utils/axios'

const namespace = 'creatorReports'

export interface ReadCreatorOverviewQueryParams {
  startAt: Date
  endAt: Date
  period: ReportPeriod
  creatorIds?: number[]
  permissionKey?: string
}

export interface ReadCreatorPerformanceQueryParams {
  startAt: Date
  endAt: Date
  period: ReportPeriod
  creatorId?: number
  permissionKey?: string
}

export const readCreatorOverviewTrends = createAppAsyncThunk(
  `${namespace}/readCreatorOverviewTrends`,
  async (params: ReadCreatorOverviewQueryParams, { getState }) => {
    const { global: { permissionKey } } = getState()
    const finalPermissionKey = params.permissionKey || permissionKey
    const { featureTimezone } = selectFeatureTimezone(getState())
    return await axios.get<CreatorOverviewTrends>('/analytics/creator-overview-trends', {
      params: {
        ...params,
        creatorIds: params.creatorIds?.join(','),
        permissionKey: finalPermissionKey,
        responseTimezone: featureTimezone,
      },
    })
  },
)

export const readCreatorOverviewChannels = createAppAsyncThunk(
  `${namespace}/readCreatorOverviewChannels`,
  async (params: ReadCreatorOverviewQueryParams, { getState }) => {
    const { global: { permissionKey } } = getState()
    const { featureTimezone } = selectFeatureTimezone(getState())
    const finalPermissionKey = params.permissionKey || permissionKey
    return await axios.get<CreatorOverviewChannels>('/analytics/creator-overview-channels', {
      params: {
        ...params,
        creatorIds: params.creatorIds?.join(','),
        permissionKey: finalPermissionKey,
        responseTimezone: featureTimezone,
      },
    })
  },
)

export const readCreatorOverviewStatistics = createAppAsyncThunk(
  `${namespace}/readCreatorOverviewStatistics`,
  async (params: ReadCreatorOverviewQueryParams, { getState }) => {
    const { global: { permissionKey } } = getState()
    const finalPermissionKey = params.permissionKey || permissionKey
    const { featureTimezone } = selectFeatureTimezone(getState())
    return await axios.get<CreatorOverviewStatistics>('/analytics/creator-overview-statistics', {
      params: {
        ...params,
        creatorIds: params.creatorIds?.join(','),
        permissionKey: finalPermissionKey,
        responseTimezone: featureTimezone,
      },
    })
  },
)

export const readCreatorPerformance = createAppAsyncThunk(
  `${namespace}/readCreatorPerformance`,
  async (params: ReadCreatorPerformanceQueryParams, { getState }) => {
    const { global: { permissionKey } } = getState()
    const finalPermissionKey = params.permissionKey || permissionKey
    const { featureTimezone } = selectFeatureTimezone(getState())
    return await axios.get<CreatorPerformance>('/analytics/creator-performance', {
      params: {
        ...params,
        permissionKey: finalPermissionKey,
        responseTimezone: featureTimezone,
      },
    })
  },
)

export const readCreatorOverviewSummary = createAppAsyncThunk(
  `${namespace}/readCreatorOverviewSummary`,
  async (
    {
      startAt,
      endAt,
      creatorIds,
      permissionKey,
    }: ReadCreatorOverviewSummaryParams = {},
    { getState },
  ) => {
    const { global: { permissionKey: globalPermissionKey } } = getState()
    const { data } = await axios.get<CreatorOverviewSummary>(
      '/analytics/creator-overview-summary', {
      params: {
        startAt,
        endAt,
        creatorIds: creatorIds?.join(','),
        permissionKey: permissionKey || globalPermissionKey,
      },
    })
    return data
  },
)

export const readTopCreators = createAppAsyncThunk(
  `${namespace}/readTopCreators`,
  async (
    {
      startAt,
      endAt,
      permissionKey,
    }: ReadCreatorOverviewSummaryParams = {},
    { getState },
  ) => {
    const { global: { permissionKey: globalPermissionKey } } = getState()
    const { data } = await axios.get<TopCreatorsSummary>(
      '/analytics/top-creators', {
      params: {
        startAt,
        endAt,
        permissionKey: permissionKey || globalPermissionKey,
      },
    })
    return data
  },
)

const initialState: {
  creatorOverviewSummary?: CreatorOverviewSummary,
  creatorOverviewTrends?: CreatorOverviewTrends,
  creatorOverviewChannels?: CreatorOverviewChannels,
  creatorOverviewStatistics?: CreatorOverviewStatistics
  creatorPerformance?: CreatorPerformance
} = {}


const reducer = createReducer(initialState, (builder) => {
  builder.addCase(readCreatorOverviewSummary.fulfilled, (draft, { payload }) => {
    draft.creatorOverviewSummary = payload
  })
  builder.addCase(readCreatorOverviewTrends.fulfilled, (draft, { payload }) => {
    draft.creatorOverviewTrends = payload.data
  })

  builder.addCase(readCreatorOverviewChannels.fulfilled, (draft, { payload }) => {
    draft.creatorOverviewChannels = payload.data
  })

  builder.addCase(readCreatorOverviewStatistics.fulfilled, (draft, { payload }) => {
    draft.creatorOverviewStatistics = payload.data
  })

  builder.addCase(readCreatorPerformance.fulfilled, (draft, { payload }) => {
    draft.creatorPerformance = payload.data
  })
})

export default { [namespace]: reducer }

